import { useEffect, useState } from "react";
import { View } from "react-native";
import { useTranslation } from "react-i18next";
import { MAX_SESSIONS, type Draft } from "@heraion/shared";

import { Button } from "../primitives/Button";
import { Dialog } from "../primitives/Dialog";
import { SheetMenu } from "../primitives/SheetMenu";
import { TextField } from "../primitives/TextField";
import type { UiTheme } from "../theme";
import type { V3Styles } from "./v3Styles";
import { CopyOntoTray } from "./CopyOntoTray";
import { dayLabel, type V3DayActions } from "./useV3DayActions";

export type DayMenuMode = "menu" | "copy" | "rename" | null;

// The day menu (#336, brief §7.2 «Menu del giorno»): duplicate, copy onto,
// move, rename, clear, remove. The list is a bottom Sheet; «Copy onto…»
// and rename are Dialogs, opened as the list closes.
export function DayMenu({
  theme,
  styles,
  draft,
  sIdx,
  mode,
  setMode,
  days,
}: {
  theme: UiTheme;
  styles: V3Styles;
  draft: Draft;
  sIdx: number;
  mode: DayMenuMode;
  setMode: (m: DayMenuMode) => void;
  days: V3DayActions;
}) {
  const { t } = useTranslation();
  const count = draft.sessions.length;
  const day = dayLabel(draft, sIdx);
  const [name, setName] = useState("");
  useEffect(() => {
    if (mode === "rename") setName(draft.sessions[sIdx]?.name ?? "");
  }, [mode, draft.sessions, sIdx]);
  const close = () => setMode(null);

  return (
    <>
      <SheetMenu
        theme={theme}
        visible={mode === "menu"}
        onClose={close}
        title={day}
        closeLabel={t("program.cancel", { defaultValue: "Cancel" })}
        items={[
          { key: "dup", icon: "copy", label: t("editorV3.duplicateDay", { defaultValue: "Duplicate day" }), onPress: () => days.duplicate(sIdx), disabled: count >= MAX_SESSIONS },
          { key: "copy", icon: "arrow-right", label: t("editorV3.copyOnto", { defaultValue: "Copy onto…" }), onPress: () => setMode("copy"), disabled: count < 2 },
          { key: "up", icon: "chevron-up", label: t("editorV3.moveDayEarlier", { defaultValue: "Move earlier" }), onPress: () => days.move(sIdx, -1), disabled: sIdx === 0 },
          { key: "down", icon: "chevron-down", label: t("editorV3.moveDayLater", { defaultValue: "Move later" }), onPress: () => days.move(sIdx, 1), disabled: sIdx >= count - 1 },
          { key: "rename", icon: "edit", label: t("editorV3.renameDay", { defaultValue: "Rename" }), onPress: () => setMode("rename") },
          { key: "clear", icon: "x", label: t("editorV3.clear", { defaultValue: "Clear day" }), onPress: () => days.clear(sIdx), destructive: true },
          { key: "remove", icon: "trash", label: t("editorV3.removeDay", { defaultValue: "Remove day" }), onPress: () => days.remove(sIdx), destructive: true, disabled: count <= 1 },
        ]}
      />
      {mode === "copy" ? (
        <CopyOntoTray
          theme={theme}
          question={t("editorV3.copyOntoQuestion", { day, defaultValue: "Copy {{day}} onto which day?" })}
          options={draft.sessions
            .map((_, i) => ({ key: String(i), label: dayLabel(draft, i) }))
            .filter((o) => o.key !== String(sIdx))}
          onChoose={(key) => {
            close();
            days.copyOnto(sIdx, Number(key));
          }}
          footnote={t("editorV3.copyOntoFootnote", {
            defaultValue: "The day you pick loses what it has now.",
          })}
          onCancel={close}
        />
      ) : null}
      <Dialog
        theme={theme}
        visible={mode === "rename"}
        onRequestClose={close}
        title={t("editorV3.renameTitle", { day, defaultValue: "Rename {{day}}" })}
      >
        <TextField
          theme={theme}
          value={name}
          onChangeText={setName}
          autoFocus
          accessibilityLabel={t("editorV3.dayName", { defaultValue: "Day name" })}
          maxLength={60}
        />
        <View style={styles.dialogActions}>
          <Button theme={theme} variant="ghost" label={t("program.cancel", { defaultValue: "Cancel" })} onPress={close} />
          <Button
            theme={theme}
            label={t("program.save", { defaultValue: "Save" })}
            // A day needs a name to save (sessionNameEmpty).
            disabled={name.trim() === ""}
            onPress={() => {
              days.rename(sIdx, name.trim());
              close();
            }}
          />
        </View>
      </Dialog>
    </>
  );
}
